import { PaginationButtonsProps } from 'components/shared'
import { SelectOption } from 'components/shared/Select'
import { action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { Attribute, Page, SoldProductSkuDTO } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { PRODUCT_STORE } from 'store/ProductStore'
import { RootStore } from 'store/RootStore'
import { Api, FilterRange, initialFilterRange, SortDirection } from 'utilities'
import {
  AttributeMap,
  initialSkuSearchProps, initialSkuTablePaginationProps,
  SkuRequestDTO, SkuTablePaginationProps, SkuTableSortProps
} from './ProductSkuStore.types'

export const PRODUCT_SKU_STORE = 'PRODUCT_SKU_STORE'

export class ProductSkuStore {
  rootStore: RootStore
  timeoutId: NodeJS.Timeout

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable skus: SoldProductSkuDTO[] = []

  @observable skuSearchProps: SkuRequestDTO = initialSkuSearchProps
  @observable pagination: SkuTablePaginationProps = initialSkuTablePaginationProps
  @observable priceRange: FilterRange = initialFilterRange
  @observable stockRange: FilterRange = initialFilterRange
  @observable soldRange: FilterRange = initialFilterRange
  @observable revenueRange: FilterRange = initialFilterRange

  @observable attributeMap: AttributeMap = null

  @action setSkus = (skus: SoldProductSkuDTO[]) => {
    this.skus = skus
  }

  @action setPaginationProps = (props: Partial<SkuTablePaginationProps>) => {
    this.pagination = {
      ...this.pagination,
      ...props
    }
  }

  @action setSearchProps = (props: Partial<SkuRequestDTO>) => {
    this.skuSearchProps = {
      ...this.skuSearchProps,
      ...props
    }
  }

  @action setPriceRange = (priceRange: FilterRange) => {
    this.priceRange = priceRange
  }

  @action setStockRange = (stockRange: FilterRange) => {
    this.stockRange = stockRange
  }

  @action setSoldRange = (soldRange: FilterRange) => {
    this.soldRange = soldRange
  }

  @action setRevenueRange = (revenueRange: FilterRange) => {
    this.revenueRange = revenueRange
  }

  @action setAttributeMap = (attributeMap: AttributeMap) => {
    this.attributeMap = attributeMap
  }

  getSearchedProductSkus = (resetPageNumber: boolean = false) => {
    if (!this.productId) return

    const {
      [GENERAL_STORE]: {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      }
    } = this.rootStore

    setLoadingOn()

    Api
      .post(
        `/product/${this.productId
        }/productSkus/all?page=${resetPageNumber ? 0 : this.pagination.page
        }&size=${this.pagination.size
        }&sort=${this.pagination.sortProp},${this.pagination.sortDirection}`,
        this.skuSearchProps
      )
      .then((data: Page<SoldProductSkuDTO>) => {
        if (JSON.stringify(this.priceRange) === JSON.stringify(initialFilterRange)) {
          let minPrice, maxPrice, minStock, maxStock, minSold, maxSold, minRevenue, maxRevenue
          const attrMap: AttributeMap = new Map()

          data.content.forEach(sku => {
            minPrice = minPrice ? Math.min(minPrice, sku.price) : sku.price
            maxPrice = maxPrice ? Math.max(maxPrice, sku.price) : sku.price

            minStock = minStock ? Math.min(minStock, sku.stock) : sku.stock
            maxStock = maxStock ? Math.max(maxStock, sku.stock) : sku.stock

            minSold = minSold ? Math.min(minSold, sku.soldCount) : sku.soldCount
            maxSold = maxSold ? Math.max(maxSold, sku.soldCount) : sku.soldCount

            minRevenue = minRevenue ? Math.min(minRevenue, sku.total) : sku.total
            maxRevenue = maxRevenue ? Math.max(maxRevenue, sku.total) : sku.total

            const { attributes } = sku
            if (attributes && attributes.length) {
              attributes.forEach(attr => {
                const attrMapValue = attrMap.get(attr.attributeName) ||
                  new Map<number, Attribute>()
                attrMapValue.set(attr.id, attr)
                attrMap.set(attr.attributeName, attrMapValue)
              })
            }
          })

          this.setPriceRange({ min: minPrice, max: maxPrice })
          this.setStockRange({ min: minStock, max: maxStock })
          this.setSoldRange({ min: minSold, max: maxSold })
          this.setRevenueRange({ min: minRevenue, max: maxRevenue })

          this.setAttributeMap(attrMap)
        }

        this.setSkus(data.content)
        this.setPaginationProps({
          page: data.number,
          size: data.size,
          totalElements: data.totalElements,
          totalPages: data.totalPages
        })
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  setSortDirection = (sortProp: SkuTableSortProps, sortDirection: SortDirection) => {
    this.setPaginationProps({
      sortProp,
      sortDirection
    })
    this.getSearchedProductSkus(true)
  }

  onPageSelect = (pageNumber: number) => {
    this.setPaginationProps({ page: pageNumber })
    this.getSearchedProductSkus()
  }

  onSearchPropsChange = (newProps: Partial<SkuRequestDTO>) => {
    this.setSearchProps(newProps)

    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }

    const needTimeoutProps: Array<keyof SkuRequestDTO> = [
      'sku', 'maxPrice', 'maxQuantity', 'maxRevenue', 'maxSold',
      'minPrice', 'minQuantity', 'minRevenue', 'minSold'
    ]

    if (needTimeoutProps.includes(Object.keys(newProps)[0] as any)) {
      this.timeoutId = setTimeout(() => this.getSearchedProductSkus(true), 800)
    } else {
      this.getSearchedProductSkus(true)
    }
  }

  onPriceFilterChange = ([minPrice, maxPrice]: number[]) => {
    this.onSearchPropsChange({ minPrice, maxPrice })
  }

  onStockFilterChange = ([minQuantity, maxQuantity]: number[]) => {
    this.onSearchPropsChange({ minQuantity, maxQuantity })
  }

  onSoldFilterChange = ([minSold, maxSold]: number[]) => {
    this.onSearchPropsChange({ minSold, maxSold })
  }

  onRevenueFilterChange = ([minRevenue, maxRevenue]: number[]) => {
    this.onSearchPropsChange({ minRevenue, maxRevenue })
  }

  onAttributeFilterSelect = (attributeName: string, id: number) => {
    const attributes = (this.skuSearchProps.attributes || [])
      .filter(attr => attr.attributeName !== attributeName)
    id && attributes.push({ id, attributeName } as Attribute)
    this.onSearchPropsChange({ attributes })
  }

  onProductsTableDidMount = () => {
    this.setPriceRange(initialFilterRange)
    this.setStockRange(initialFilterRange)
    this.setSoldRange(initialFilterRange)
    this.setRevenueRange(initialFilterRange)
    this.setSearchProps(initialSkuSearchProps)
    this.setPaginationProps(initialSkuTablePaginationProps)
  }

  @computed get paginationInfo (): PaginationButtonsProps {
    return {
      elementsPerPage: this.pagination.size,
      selectedPageNumber: this.pagination.page,
      totalElements: this.pagination.totalElements,
      totalPages: this.pagination.totalPages
    }
  }

  @computed get productId (): number {
    return this.rootStore[PRODUCT_STORE].productToView.id
  }

  @computed get totalSkuCount (): number {
    const { numberSkus } = this.rootStore[PRODUCT_STORE].productToView
    return numberSkus || 0
  }

  @computed get hasSkus (): boolean {
    return this.totalSkuCount > 0
  }

  @computed get productSkus (): any {
    return this.skus;
  }

  @computed get attributeLabels (): string[] {
    if (this.attributeMap === null) return []

    return Array.from(this.attributeMap.keys())
  }

  @computed get attributesCount (): number {
    return this.attributeLabels.length
  }

  @observable isColumnMarked = computedFn(
    (sortProp: SkuTableSortProps): boolean => {
      return this.pagination.sortProp === sortProp
    }
  )

  @observable getSortDirection = computedFn(
    (sortProp: SkuTableSortProps): SortDirection => {
      if (this.isColumnMarked(sortProp)) {
        return this.pagination.sortDirection
      }
      return SortDirection.DESC
    }
  )

  @observable getSelectOptionsByAttrLabel = computedFn(
    (attrLabel: string): Array<SelectOption<any>> => {
      if (this.attributeMap === null) return []

      return Array.from(this.attributeMap.get(attrLabel).values())
        .map(attr => ({
          label: attr.name, value: attr.id
        }))
    }
  )

  @observable getAttrFilterValueByAttrLabel = computedFn(
    (attrLabel: string): number | string => {
      const { attributes } = this.skuSearchProps

      if (!attributes || !attributes.length) return ''

      const selectedAttr = attributes.find(attr => attr.attributeName === attrLabel)

      if (!selectedAttr) return ''

      return selectedAttr.id
    }
  )
}
